import React, {ReactElement, useLayoutEffect, useRef, useState} from 'react';
import {SHAPE_COLOR_DEFAULT} from '../../../constants';
import {IElementEllipse} from '../../../incase-domain/elements/shapes/ellipse';

interface ElementEllipseComponentProps {
    element: IElementEllipse;
}

function ElementEllipseComponent({element}: ElementEllipseComponentProps): ReactElement {
    const {color, fill, isCircle, thickness = 0.5} = element;
    const containerRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState({width: 0, height: 0, fontSizePx: 16});

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;

        const updateSize = (nextWidth: number, nextHeight: number): void => {
            const fontSizePx = parseFloat(window.getComputedStyle(el).fontSize) || 16;
            setSize((prev) =>
                Math.abs(prev.width - nextWidth) < 0.5 &&
                Math.abs(prev.height - nextHeight) < 0.5 &&
                Math.abs(prev.fontSizePx - fontSizePx) < 0.5
                    ? prev
                    : {width: nextWidth, height: nextHeight, fontSizePx}
            );
        };

        updateSize(el.offsetWidth, el.offsetHeight);

        if (typeof ResizeObserver === 'undefined') return undefined;

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            updateSize(entry.contentRect.width, entry.contentRect.height);
        });
        resizeObserver.observe(el);

        return () => resizeObserver.disconnect();
    }, []);

    const stroke = color || SHAPE_COLOR_DEFAULT;
    const {width, height, fontSizePx} = size;
    const thicknessPx = fontSizePx * thickness;
    const rx = Math.max(0, (width - thicknessPx) / 2);
    const ry = Math.max(0, (height - thicknessPx) / 2);
    const r = Math.min(rx, ry);

    return (
        <div ref={containerRef} className={isCircle ? 'c-circle' : 'c-ellipse'}>
            {width > 0 && height > 0 && (
                <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
                    {isCircle ? (
                        <circle
                            cx={width / 2}
                            cy={height / 2}
                            r={r}
                            fill={fill ? stroke : 'none'}
                            stroke={stroke}
                            strokeWidth={thicknessPx}
                        />
                    ) : (
                        <ellipse
                            cx={width / 2}
                            cy={height / 2}
                            rx={rx}
                            ry={ry}
                            fill={fill ? stroke : 'none'}
                            stroke={stroke}
                            strokeWidth={thicknessPx}
                        />
                    )}
                </svg>
            )}
        </div>
    );
}

export default ElementEllipseComponent;
